import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import {
  IFailedPayload,
  IUploadRequest,
  IUploadResponse,
} from 'redux/types/BulkUploadAgent.types';
import { agentEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import {
  bulkUploadAgentFile,
  bulkUploadAgentFileFailure,
  bulkUploadAgentFileSuccess,
} from '../../slice/agent/BulkUploadAgent.slice';

// Saga for bulk uploading agent data
function* bulkUploadAgentSaga(action: PayloadAction<IUploadRequest>) {
  try {
    const { file, allocatorIds, products, buckets, agencyId } = action.payload;

    const formData = new FormData();
    formData.append('file', file);

    if (agencyId) {
      formData.append('agencyId', agencyId);
    }

    if (allocatorIds && allocatorIds.length > 0) {
      allocatorIds.forEach((id) => {
        formData.append('allocatorIds', id);
      });
    }

    if (products && products.length > 0) {
      products.forEach((product) => {
        formData.append('products', product);
      });
    }

    if (buckets && buckets.length > 0) {
      buckets.forEach((bucket) => {
        formData.append('buckets', bucket);
      });
    }

    const response: AxiosResponse<IUploadResponse> =
      yield HTTPClient.PostRequest(
        `${agentEndpoints.BULK_UPLOAD_AGENT}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

    const responseData = response.data;

    // Check if the response contains errors, even though it's a 200 status
    if (responseData.errorCount === 0) {
      // No errors - true success case
      yield put(bulkUploadAgentFileSuccess(responseData));
      message.success(
        `Bulk upload successful. ${responseData.totalRows} records processed.`
      );
    } else {
      // Response has an errorCount > 0, treat as a partial success/failure
      const failedPayload: IFailedPayload = {
        message: 'Upload completed with validation errors',
        response: responseData,
      };

      yield put(bulkUploadAgentFileFailure(failedPayload));

      message.warning(
        `Upload completed with ${responseData.errorCount} errors out of ${responseData.totalRows} records.`
      );
    }
  } catch (error: unknown) {
    // Check if the error response contains our expected format
    const typedError = error as { response?: { data?: unknown } };
    const errorData = typedError?.response?.data;

    if (
      errorData &&
      typeof errorData === 'object' &&
      'totalRows' in errorData
    ) {
      // This is our custom error format with validation details
      const typedErrorData = errorData as IUploadResponse;
      const failedPayload: IFailedPayload = {
        message: 'Upload failed with validation errors',
        response: typedErrorData,
      };

      yield put(bulkUploadAgentFileFailure(failedPayload));

      message.error(
        `Upload failed with ${typedErrorData.errorCount} errors out of ${typedErrorData.totalRows} records.`
      );
    } else {
      // Generic error without our custom format
      const errorMessage =
        typedError instanceof Error ? typedError.message : 'Unknown error';

      yield put(
        bulkUploadAgentFileFailure({
          message: errorMessage || 'Bulk upload failed',
        })
      );

      message.error(`Bulk upload failed: ${errorMessage || 'Unknown error'}`);
    }
  }
}

// Watcher saga for bulk upload action
export function* watchBulkUploadAgentSaga() {
  yield takeLatest(bulkUploadAgentFile.type, bulkUploadAgentSaga);
}
